import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import { ExportContractInquiryPaymentTermDto } from "@models/export/exportModel"
import contractInquiryService from "@services/contract/contractInquiryService"
import type { IContractPaymentScheduleOverview } from "@services/contract/contractInquiryService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type { IContractInquiry } from "@stores/contract/contractInquiryModel"
import type { IContractInquiryUpdateInput } from "@models/contract/contractInquiryUpdateModel"
import { action, makeAutoObservable, observable, runInAction } from "mobx"

class ContractInquiryStore {
  @observable isLoading!: boolean
  @observable tableContractData!: PagedResultDto<any>
  @observable tableReceiptData!: PagedResultDto<IContractInquiry>
  @observable tablePlanData!: any[]
  @observable detailData!: IContractInquiry
  @observable contractPaymentScheduleData!: any[]
  @observable listContractEvent!: any[]
  @observable listContractComboBox!: any[]
  @observable previewReceiptAccountingData!: any[]
  @observable tableAccountingIntegrationData!: PagedResultDto<any>
  @observable tableInvoiceIntegrationData!: PagedResultDto<any>
  @observable previewReceiptData!: any[]
  @observable phoebusInvoiceData!: PagedResultDto<any>
  @observable receiptListByContract!: any[]
  @observable contractPaymentScheduleOverview!: IContractPaymentScheduleOverview | null
  constructor() {
    makeAutoObservable(this)

    this.tableContractData = { items: [], totalCount: 0 }
    this.tableReceiptData = { items: [], totalCount: 0 }
    this.tablePlanData = []
    this.contractPaymentScheduleData = []
    this.detailData = {} as IContractInquiry
    this.listContractComboBox = []
    this.listContractEvent = []
    this.previewReceiptAccountingData = []
    this.tableAccountingIntegrationData = { items: [], totalCount: 0 }
    this.tableInvoiceIntegrationData = { items: [], totalCount: 0 }
    this.previewReceiptData = []
    this.phoebusInvoiceData = { items: [], totalCount: 0 }
    this.receiptListByContract = []
    this.contractPaymentScheduleOverview = null
  }

  @action
  async createReceipt(body: any) {
    this.isLoading = true
    try {
      const result = await contractInquiryService.create(body)
      runInAction(() => {
        this.isLoading = false
      })
      return result
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
      })
      console.error("Lỗi trong store khi tạo phiếu thu:", error)
      throw error
    }
  }

  // Lấy tổng giá trị đã thanh toán của booking (mặc định số tiền cho phiếu hoàn)
  @action
  async getTotalPaidAmountByBooking(bookingId: number): Promise<number> {
    const result = await contractInquiryService.getTotalPaidAmountByBookingId(
      bookingId
    )
    return result ?? 0
  }

  @action
  async updateReceipt(body: IContractInquiryUpdateInput) {
    this.isLoading = true
    try {
      const result = await contractInquiryService.update(body)
      runInAction(() => {
        this.detailData = result
        this.isLoading = false
      })
      return result
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
      })
      throw error
    }
  }

  /**
   * Feature-0378 — xóa 1 phiếu thu. Lỗi nghiệp vụ của BE (đã đồng bộ, phiếu chuyển cọc, còn
   * chứng từ tham chiếu) đã được interceptor toast sẵn nên chỉ ném lại cho scene xử lý UI.
   */
  @action
  async deleteReceipt(id: number, deleteReason: string) {
    this.isLoading = true
    try {
      await contractInquiryService.delete(id, deleteReason)
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /** Feature-0378 — xóa cả lô phiếu cùng `transactionCode`. */
  @action
  async deleteReceiptByTransactionCode(
    transactionCode: string,
    deleteReason: string
  ) {
    this.isLoading = true
    try {
      await contractInquiryService.deleteByTransactionCode(
        transactionCode,
        deleteReason
      )
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  @action
  async calculationContractFeeList(body: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .calculationContractFeeList(body)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async getAllInquiryContract(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllInquiryContract(params)
      .finally(() => (this.isLoading = false))
    this.tableContractData = result
  }

  @action
  public exportContractInquiryPaymentTerm = async (
    body: ExportContractInquiryPaymentTermDto
  ) => {
    this.isLoading = true
    await contractInquiryService
      .exportContractInquiryPaymentTerm(body)
      .finally(() => (this.isLoading = false))
    notifySuccess(
      LNotification("SUCCESSFULLY"),
      LNotification("EXPORT_SUCCESSFULLY")
    )
  }
  @action
  public exportContractInquiry = async (body: any) => {
    this.isLoading = true
    await contractInquiryService
      .exportContractInquiry(body)
      .finally(() => (this.isLoading = false))
    notifySuccess(
      LNotification("SUCCESSFULLY"),
      LNotification("EXPORT_SUCCESSFULLY")
    )
  }

  @action
  public exportInvoiceIntegration = async (body: any) => {
    this.isLoading = true
    await contractInquiryService
      .exportInvoiceIntegration(body)
      .finally(() => (this.isLoading = false))
    notifySuccess(
      LNotification("SUCCESSFULLY"),
      LNotification("EXPORT_SUCCESSFULLY")
    )
  }

  @action
  public exportAccountingIntegration = async (body: any) => {
    this.isLoading = true
    await contractInquiryService
      .exportAccountingIntegration(body)
      .finally(() => (this.isLoading = false))
    notifySuccess(
      LNotification("SUCCESSFULLY"),
      LNotification("EXPORT_SUCCESSFULLY")
    )
  }

  @action
  async getAllContractInquiry(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllContractInquiry(params)
      .finally(() => (this.isLoading = false))
    this.tableReceiptData = result
  }

  @action
  async getAllContractInquiryBooking(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllContractInquiryBooking(params)
      .finally(() => (this.isLoading = false))
    this.tableReceiptData = result
  }
  @action
  async getListContractComboBox() {
    this.isLoading = true
    const result = await contractInquiryService
      .getListContractComboBox()
      .finally(() => (this.isLoading = false))
    this.listContractComboBox = result
  }

  @action
  async emptyTablePlanData() {
    this.tablePlanData = []
  }
  @action
  async getPlanReceiptAllocation(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getPlanReceiptAllocation(params)
      .finally(() => (this.isLoading = false))
    this.tablePlanData = result
  }

  @action
  async getReturnAllocation(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getReturnAllocation(params)
      .finally(() => (this.isLoading = false))
    this.tablePlanData = result
  }

  @action
  async getPaymentSlipFromNegativePeriods(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getPaymentSlipFromNegativePeriods(params)
      .finally(() => (this.isLoading = false))
    this.tablePlanData = result
  }

  @action
  async get(id: number) {
    this.isLoading = true
    try {
      const result = await contractInquiryService.get(id)
      runInAction(() => {
        this.detailData = result
        this.isLoading = false
      })
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
      })
      throw error
    }
  }
  // Contract payment schedule
  @action
  async getListContractInquiryWithContract(contractId: number) {
    this.isLoading = true
    const result = await contractInquiryService
      .getListContractInquiryWithContract(contractId)
      .finally(() => (this.isLoading = false))
    this.contractPaymentScheduleData = result
  }

  // Overview 7 chỉ số tổng hợp lịch thu tiền (BE tính sẵn)
  @action
  async getContractPaymentScheduleOverview(contractId: number) {
    this.isLoading = true
    const result = await contractInquiryService
      .getContractPaymentScheduleOverview(contractId)
      .finally(() => (this.isLoading = false))
    this.contractPaymentScheduleOverview = result
  }

  @action
  async getListContractEventApprovedWithContract(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getListContractEventApprovedWithContract(params)
      .finally(() => (this.isLoading = false))
    this.listContractEvent = result
  }

  @action
  async getAllListReceiptAccounting(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllListReceiptAccounting(params)
      .finally(() => (this.isLoading = false))
    this.tableReceiptData = result
  }

  @action
  async previewReceiptAccounting(params: any, id: number[]) {
    this.isLoading = true
    const result = await contractInquiryService
      .previewReceiptAccounting(params, id)
      .finally(() => (this.isLoading = false))
    this.previewReceiptAccountingData = result
  }

  @action
  async previewReceipt(id: number[]) {
    this.isLoading = true
    const result = await contractInquiryService
      .previewReceipt(id)
      .finally(() => (this.isLoading = false))
    this.previewReceiptData = result
  }

  @action
  async getAllAccountingIntegration(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllAccountingIntegration(params)
      .finally(() => (this.isLoading = false))
    this.tableAccountingIntegrationData = result
  }

  @action
  async getAllInvoiceIntegration(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllInvoiceIntegration(params)
      .finally(() => (this.isLoading = false))
    this.tableInvoiceIntegrationData = result
  }

  @action
  async syncInvoiceIntegration(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .syncInvoiceIntegration(params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async syncAccountingIntegration(params: any, body: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .syncAccountingIntegration(params, body)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async resendSyncReceiptInvoice(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .resendSyncReceiptInvoice(params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async resendSyncReceiptAccounting(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .resendSyncAccountingIntegration(params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async GetPhoebusInvoice(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .GetPhoebusInvoice(params)
      .finally(() => (this.isLoading = false))
    this.phoebusInvoiceData = result
    return result
  }

  @action
  async clearContractInquiryToReApply(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .clearContractInquiryToReApply(params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async getAllocationWithContract(params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .getAllocationWithContract(params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async insertContractInquiryDetail(body: any, params: any) {
    this.isLoading = true
    const result = await contractInquiryService
      .insertContractInquiryDetail(body, params)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async downloadImportReceiptTemplate() {
    this.isLoading = true
    await contractInquiryService
      .downloadImportReceiptTemplate()
      .finally(() => (this.isLoading = false))
  }

  @action
  async validateBeforeImportReceipt(file: File) {
    this.isLoading = true
    const result = await contractInquiryService
      .validateBeforeImportReceipt(file)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  async importMigrationReceipt(file: File) {
    this.isLoading = true
    const result = await contractInquiryService
      .importMigrationReceipt(file)
      .finally(() => (this.isLoading = false))
    return result
  }

  // Lấy danh sách phiếu thu đã có của hợp đồng (cơ sở tạo phiếu chi / điều chỉnh)
  @action
  async getReceiptListByContract(contractId: number) {
    this.isLoading = true
    const result = await contractInquiryService
      .getReceiptListByContract(contractId)
      .finally(() => (this.isLoading = false))
    this.receiptListByContract = result ?? []
    return result
  }

  @action
  clearReceiptListByContract() {
    this.receiptListByContract = []
  }
}

export default ContractInquiryStore
